import { useState, useMemo, useEffect } from 'react';

import { useMutation } from '@tanstack/react-query';
import isEmpty from 'lodash/isEmpty';

import { CustomNotification, dialogFunction } from 'common/components';

import { useGetMemberId } from 'hooks/useGetMemberId';
import { useCallbackRef, useCheckPermissions } from 'hooks';
import {
  useCheckInvoicedPayment,
  useShowWarningWhenNoCreditCard,
} from 'hooks/home/useCreditCardAlert';
import {
  useQueryBillingNote,
  useQuerySavedMemberCard,
  useRefetchAllMemberBillingStatement,
} from './queries';
import { useQueryBillingProperties } from 'pages/check-credit-card-info/queries';

import * as paymentServices from 'services/payment';

import { parseJSON } from 'utils';
import { sleep } from 'utils/delay';

import {
  EDIT_MEMBER_PERMISSION,
  PERMISSION_EDIT_COMPANY_PROFILE,
} from 'static/Permission';

import { useUserInfo } from 'hooks/auth/useUserInfo';

export const useListenerMessageSaveCard = ({
  offModal,
  fetchSavedMemberCard,
  modeAction,
  onSuccessCallback,
}) => {
  const { memberId } = useGetMemberId();

  const { updateUserInfo } = useUserInfo();

  const { closeCreditCardMissingCard } = useShowWarningWhenNoCreditCard();
  const refetchAllMemberBillingStatement =
    useRefetchAllMemberBillingStatement();
  const { invalidateBillingProperties } = useQueryBillingProperties({
    memberId,
    enabled: false,
  });
  const { invalidateBillingNote } = useQueryBillingNote({
    memberId,
    enabled: false,
  });

  const handleListenerMessage = useCallbackRef(async (eventMessage) => {
    const { isSuccess } = parseJSON(eventMessage.data);

    if (isSuccess) {
      try {
        const params = {
          memberId,
          paymentEndpointType: null,
        };

        const responseSync = await paymentServices.syncSavedMemberCard(params);

        if (responseSync?.isSuccess) {
          const fetchCardResponse = await fetchSavedMemberCard();
          const { memberSaveCards } = fetchCardResponse || {};

          if (memberSaveCards) {
            modeAction === 'add'
              ? CustomNotification.success(
                  'Add Payment Information successfully!'
                )
              : CustomNotification.success(
                  'Edit Payment Information successfully!'
                );

            //* update user info
            updateUserInfo();
            onSuccessCallback && onSuccessCallback();
            closeCreditCardMissingCard();
            invalidateBillingProperties();
            invalidateBillingNote();
            refetchAllMemberBillingStatement();
          } else {
            modeAction === 'add'
              ? CustomNotification.error('Failed to add Payment Information!')
              : CustomNotification.error('Failed to edit Payment Information!');
          }

          offModal();
        }
      } catch (error) {
        CustomNotification.error(error?.message ?? 'Something went wrong!');
      }
    }
  });

  useEffect(() => {
    window.addEventListener('message', handleListenerMessage);

    return () => {
      window.removeEventListener('message', handleListenerMessage);
    };
  }, [handleListenerMessage]);
};

export const useGetSavedMemberCard = () => {
  const { memberId } = useGetMemberId();

  const {
    data: savedMemberCardData,
    isLoading,
    fetchSavedMemberCard,
  } = useQuerySavedMemberCard({
    memberId,
  });

  const savedMemberCard = savedMemberCardData
    ? savedMemberCardData.memberSaveCards?.[0]
    : {};

  return {
    savedMemberCard,
    isFetchingSavedCard: isLoading,
    fetchSavedMemberCard,
    hasCreditCard: !isEmpty(savedMemberCard),
  };
};

export const useMemberPaymentActions = ({
  memberPaymentProfileId,
  fetchSavedMemberCard,
  offModal,
}) => {
  const [iframeResponse, setIframeResponse] = useState({
    value: null,
    status: 'idle',
  });

  const { memberId } = useGetMemberId();
  const { updateUserInfo, userInfo } = useUserInfo();

  const { showWarningWhenNoCreditCard, closeCreditCardMissingCard } =
    useShowWarningWhenNoCreditCard();

  const { isInvoicedTerm, isLoading } = useCheckInvoicedPayment();

  const isCurrentUserBelongToMember = memberId === userInfo?.companyId;

  const handleSaveCard = async ({ modeAction }) => {
    setIframeResponse((prevVal) => ({
      ...prevVal,
      status: 'loading',
    }));

    try {
      const paramsAddCard = {
        memberId,
        paymentEndpointType: null,
      };

      const paramsEditCard = {
        memberId,
        memberPaymentProfileId,
      };

      const responseIframe =
        modeAction === 'add'
          ? await paymentServices.getLinkIframeCard(paramsAddCard)
          : await paymentServices.getLinkIframeEditedCard(paramsEditCard);

      if (responseIframe?.isSuccess) {
        setIframeResponse((prevVal) => ({
          ...prevVal,
          value: responseIframe?.data?.iframeUrl,
        }));

        await sleep(3000);
        setIframeResponse((prevVal) => ({
          ...prevVal,
          status: 'success',
        }));

        updateUserInfo();
      } else {
        CustomNotification.error(
          responseIframe?.message ?? 'Something went wrong!'
        );
        setIframeResponse((prevVal) => ({
          ...prevVal,
          value: null,
          status: 'error',
        }));
        offModal();
      }
    } catch (error) {
      CustomNotification.error(
        'Fail to get Payment Information, please try again!'
      );
      setIframeResponse((prevVal) => ({
        ...prevVal,
        value: null,
        status: 'error',
      }));
      offModal();
    }
  };

  const checkShowWarningMissingCard = () => {
    if (!isLoading) {
      if (isInvoicedTerm || !isCurrentUserBelongToMember) {
        closeCreditCardMissingCard();
      } else {
        showWarningWhenNoCreditCard();
      }
    }
  };

  const handleRemoveSavedCard = async () => {
    dialogFunction({
      type: 'warn',
      content: 'Are you sure you want to delete the Payment Information?',
      okText: 'Delete',
      okButtonProps: {
        type: 'danger',
      },
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          const params = {
            memberId,
            memberPaymentProfileId,
            paymentEndpointType: null,
          };
          const response = await paymentServices.removeSavedMemberCard(params);

          if (response?.isSuccess) {
            const fetchCardResponse =
              await fetchSavedMemberCard(fetchSavedMemberCard);

            const { memberSaveCards } = fetchCardResponse || {};

            if (memberSaveCards) {
              CustomNotification.success(
                'Delete Payment Information successfully!'
              );
              updateUserInfo();
              checkShowWarningMissingCard();
            } else {
              CustomNotification.error('Something went wrong!');
            }
          } else {
            CustomNotification.error(
              response?.message || 'Something went wrong!'
            );
          }
        } catch (error) {
          CustomNotification.error(error?.message ?? 'Something went wrong!');
        }
      },
    });
  };

  return {
    iframeUrl: iframeResponse.value,
    loadingIframe:
      iframeResponse.status === 'idle' || iframeResponse.status === 'loading',
    handleSaveCard,
    handleRemoveSavedCard,
  };
};

export const useVisibleModal = () => {
  const [visible, setVisible] = useState(false);

  const handler = useMemo(
    () => ({
      onModal: () => setVisible(true),
      offModal: () => setVisible(false),
      toggle: () => setVisible((modal) => !modal),
    }),
    []
  );

  return [visible, handler];
};

export const useCheckEditMemberCreditCardPermission = () => {
  const { userInfo } = useUserInfo();

  const { memberId } = useGetMemberId();

  const isEditCompanyProfile = useCheckPermissions(
    PERMISSION_EDIT_COMPANY_PROFILE
  );

  const isEditMember = useCheckPermissions(EDIT_MEMBER_PERMISSION);

  const checkEditMemberPermission = () => {
    const isUserMemberOfCompany = memberId === userInfo?.companyId;

    if (isUserMemberOfCompany) {
      return isEditCompanyProfile || isEditMember;
    } else return isEditMember;
  };

  return checkEditMemberPermission();
};

export const useDownloadBillingStatement = () => {
  return useMutation({
    mutationFn: async (params) => {
      try {
        return paymentServices.downloadBillingStatement(params);
      } catch (error) {
        console.log('error: ', error);
      }
    },
  });
};
